import { createSelector } from 'reselect';
import { compose, get, map } from 'lodash/fp';
import { composeProps, createAction, createPropsExtender } from '@mews/framework';
import { getOrEmptyObject, getOrFalse } from '@mews/fp';
import { Company } from '@mews-commander/data-access';
import { ApplicationState } from '@mews-commander/types';
import prefix from '../prefix';
import parentSelector from '../selector';
import { getCurrentEnterpriseLegalEnvironmentCode } from '../selectors/session/enterprise';
import { createBaseEntitySelectors, createEntitiesReducer } from './utils';
import { getAccountingEnvironmentByCode } from './accountingEnvironments';

export const NAME = 'companies';

const COMPANIES_LOADED = prefix('COMPANIES_LOADED');

export const setCompanies = (companies: Array<Company>) => createAction(COMPANIES_LOADED, { entities: companies });

export const companiesReducer = createEntitiesReducer({
    FETCH_SUCCESS: COMPANIES_LOADED,
});

export const [getCompanies, getCompaniesByIds, getCompaniesByOwnerId, getCompany] = createBaseEntitySelectors<
    ApplicationState,
    Company,
    typeof NAME
>(parentSelector, NAME);

export const getCompanyIds = createSelector(getCompanies, map('id'));

const getCompanyOptions = createSelector(getCompany, getOrEmptyObject('options'));

export const isCompanyInvoiceable = createSelector(getCompanyOptions, getOrFalse('isInvoiceable'));

export const areAddedFeesToInvoices = compose(getOrFalse('addFeesToInvoices'), getCompanyOptions);

export const areInvoicesTaxDeducted = compose(getOrFalse('addTaxDeductedPaymentToInvoices'), getCompanyOptions);

const getCurrentAccountingEnvironment = composeProps(
    getAccountingEnvironmentByCode,
    createPropsExtender('code', getCurrentEnterpriseLegalEnvironmentCode)
);

export const areInvoicesTaxDeductable = createSelector(
    getCurrentAccountingEnvironment,
    getOrFalse('allowTaxDeductedPayments')
);

export const getCompanyName = compose(get('name'), getCompany);

export const getCompanyCreditRatingBasic = compose(get('creditRatingBasic'), getCompany);

export const getCompanyClassifications = compose(get('classifications'), getCompany);
